// src/pages/Entreprise.js
import React from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { fadeIn, slideInLeft, slideInRight, scaleIn } from '../styles/animations';
import SectionTitle from '../components/ui/SectionTitle';

const EntreprisePageWrapper = styled.div`
  padding-top: 80px; // Pour compenser la hauteur du header fixe
`;

const HeroSection = styled.section`
  position: relative;
  height: 50vh;
  min-height: 400px;
  max-height: 600px;
  overflow: hidden;
  padding: 0;
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.4));
    z-index: 1;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%; /* Changé de 35rem à 100% pour occuper tout l'espace disponible */
  object-fit: cover;
  display: block; /* Élimine tout espace indésirable sous l'image */
  object-position: center 75%;

`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  transform: translateY(-50%);
  /* Suppression du padding ici */
  color: ${props => props.theme.colors.light};
  z-index: 2;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const HeroTitle = styled.h1`
  color: ${props => props.theme.colors.light};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.xxxl};
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xxl};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
  opacity: 0.9;
  max-width: 800px;
  margin: 0 auto;
  color: ${props => props.theme.colors.light};
`;

const Section = styled.section`
  padding: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.bgColor || props.theme.colors.light};
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const AboutContent = styled.div`
  animation: ${slideInLeft} 0.8s ease-out forwards;
  opacity: 0;
  
  p {
    margin-bottom: ${props => props.theme.spacing.md};
    line-height: 1.7;
  }
`;

const AboutImageContainer = styled.div`
  position: relative;
  animation: ${slideInRight} 0.8s ease-out forwards;
  opacity: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    order: -1;
    margin-bottom: ${props => props.theme.spacing.md};
  }
`;

const AboutImage = styled.img`
  width: auto;
  height: 25rem;
  object-fit: cover;
  object-position: center 25%;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.medium};
`;

const ExpertiseSection = styled.section`
  padding: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.theme.colors.gray};
`;

const ExpertiseList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: ${props => props.theme.spacing.lg};
`;

const ExpertiseItem = styled.li`
  margin-bottom: ${props => props.theme.spacing.md};
  padding-left: 2rem;
  position: relative;
  animation: ${fadeIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.5rem;
    width: 6px;
    height: 6px;
    background-color: ${props => props.theme.colors.primary};
    border-radius: 50%;
  }
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin: ${props => props.theme.spacing.lg} 0;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ImageItem = styled.div`
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
  animation: ${scaleIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  aspect-ratio: 4/3; /* Définit un ratio d'aspect cohérent */
  
  img {
    width: 100%;
    height: 100%; /* Changé de 200px à 100% pour occuper tout le conteneur parent */
    object-fit: cover;
    transition: transform ${props => props.theme.transitions.medium};
    display: block; /* Élimine tout espace indésirable sous l'image */
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const Entreprise = () => {
    return (
        <EntreprisePageWrapper>
            <Helmet>
                <title>Notre Entreprise - Decko | Créateur de vos parquets de jardin</title>
                <meta name="description" content="Découvrez l'histoire et le savoir-faire de Decko, spécialiste des terrasses en bois et parquets de jardin depuis 1985." />
            </Helmet>

            <HeroSection>
                <HeroImage src="20140122_0655.webp" alt="L'équipe Decko" />
                <HeroContent>
                    <HeroTitle>Notre Entreprise</HeroTitle>
                    <HeroSubtitle>Créateur de vos parquets de jardin depuis 1985</HeroSubtitle>
                </HeroContent>
            </HeroSection>

            <Section>
                <SectionContainer>
                    <AboutGrid>
                        <AboutContent>
                            <SectionTitle text="Notre Histoire" />
                            <p>Professionnel du bois depuis 1985, j'ai fondé DeckO parce que créer et construire des parquets de jardin de qualité est un vrai métier.</p>
                            <p>Aux États-Unis, « the deck builders » (traduisez « les constructeurs de terrasses ») exploitent à grande échelle une caractéristique de l'habitat traditionnel Nord Américain.</p>
                            <p>Outre-Atlantique, cette profession est donc l'affaire de vrais spécialistes. Pourquoi pas en France ?</p>
                            <p>DeckO est spécialiste ce qui implique un savoir-faire particulier alliant une passion pour le bois, une exigence de qualité et une vision créative pour vos espaces extérieurs.</p>
                        </AboutContent>

                        <AboutImageContainer>
                            <AboutImage src="IMG_0534-1024x768-2560x1200.jpg" alt="Terrasse en bois Decko" />
                        </AboutImageContainer>
                    </AboutGrid>
                </SectionContainer>
            </Section>

            <ExpertiseSection>
                <SectionContainer>
                    <SectionTitle text="Notre Expertise" centered />

                    <ExpertiseList>
                        <ExpertiseItem delay="0.1s">
                            <strong>L'écoute et l'interprétation fidèle de vos attentes</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.2s">
                            <strong>L'imagination de l'agencement extérieur et de l'ergonomie des espaces à créer</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.3s">
                            <strong>La connaissance des supports et des sols</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.4s">
                            <strong>La conception de la structure</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.5s">
                            <strong>La réalisation des plans</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.6s">
                            <strong>La connaissance des essences exotiques et indigènes, des matières composites, des métaux adaptés à l'extérieur</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.7s">
                            <strong>L'élaboration d'un cahier des charges très exigeant sur les choix, taux d'humidité des bois, de régularité des usinages et des calibrages. Le contrôle méthodique de chaque fabrication.</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.8s">
                            <strong>La maîtrise des techniques de pose testées et éprouvées notamment la pose sans vis apparentes</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.9s">
                            <strong>La qualité des finitions</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="1s">
                            <strong>Les conseils et le suivi d'après-vente</strong>
                        </ExpertiseItem>
                    </ExpertiseList>

                    <ImagesGrid>
                        {[
                            { src: "605a2c687500001272fe3a93.webp", alt: "Réalisation Decko 1", delay: "0.1s" },
                            { src: "images_1.webp", alt: "Réalisation Decko 2", delay: "0.2s" },
                            { src: "images_2.webp", alt: "Réalisation Decko 3", delay: "0.3s" },
                            { src: "IMG_0554-1024x768-2560x1200.jpg", alt: "Réalisation Decko 4", delay: "0.4s" }
                        ].map((image, index) => (
                            <ImageItem key={index} delay={image.delay}>
                                <img src={image.src} alt={image.alt} />
                            </ImageItem>
                        ))}
                    </ImagesGrid>
                </SectionContainer>
            </ExpertiseSection>

        </EntreprisePageWrapper>
    );
};

export default Entreprise;